import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Package, Calendar, Settings, Truck, AlertCircle, Check, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function SubscriptionInfo({ userRole }) {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const response = await api.get('/tenant/subscription');
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'trial': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'past_due': return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled': return 'bg-slate-100 text-slate-800 border-slate-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getHardwareStatusIcon = (status) => {
    switch (status) {
      case 'active': return <Check className="w-4 h-4 text-green-600" />;
      case 'shipped': return <Truck className="w-4 h-4 text-blue-600" />;
      case 'pending': return <Clock className="w-4 h-4 text-amber-600" />;
      case 'replacement_needed': return <AlertCircle className="w-4 h-4 text-red-600" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Card className="border-2">
        <CardContent className="p-6">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-slate-200 rounded w-3/4"></div>
              <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!subscription) {
    return null;
  }

  const plan = subscription.pricing_plan;
  const isTrial = subscription.subscription_status === 'trial';

  return (
    <Card className="border-2" data-testid="subscription-info">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <CreditCard className="w-5 h-5 text-primary" />
            <CardTitle className="text-lg">Subscription</CardTitle>
          </div>
          <Badge className={`border ${getStatusColor(subscription.subscription_status)}`}>
            {subscription.subscription_status === 'trial' ? 'Trial' : 
             subscription.subscription_status === 'active' ? 'Active' :
             subscription.subscription_status === 'past_due' ? 'Past Due' : 'Cancelled'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Current Plan */}
        {plan ? (
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4 text-muted-foreground" />
                <span className="font-semibold">{plan.name}</span>
              </div>
              <span className="text-lg font-bold">${plan.monthly_price}/mo</span>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-3">
              {plan.includes_hardware && (
                <Badge variant="secondary" className="text-xs">
                  {plan.hardware_type}
                </Badge>
              )}
              {plan.includes_initial_setup && (
                <Badge variant="secondary" className="text-xs">
                  Managed Setup
                </Badge>
              )}
              {plan.includes_ongoing_service && (
                <Badge variant="secondary" className="text-xs">
                  Ongoing Service
                </Badge>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
            <p className="text-sm font-medium">No plan selected</p>
            <p className="text-xs mt-1">Choose a plan to unlock all features</p>
          </div>
        )}

        {/* Billing Info */}
        {subscription.next_billing_date && (
          <div className="flex items-center text-sm text-muted-foreground">
            <Calendar className="w-4 h-4 mr-2" />
            <span>Next billing: {new Date(subscription.next_billing_date).toLocaleDateString()}</span>
          </div>
        )}

        {/* Service Indicators (visible to all but especially useful for PlatformAdmin) */}
        {(subscription.setup_required || subscription.hardware_status) && (
          <div className="border-t pt-4 space-y-2">
            {subscription.setup_required && !subscription.setup_completed_at && (
              <div className="flex items-center text-sm">
                <Settings className="w-4 h-4 mr-2 text-amber-500" />
                <span className="text-amber-700">Setup pending</span>
              </div>
            )}
            
            {subscription.setup_completed_at && (
              <div className="flex items-center text-sm">
                <Check className="w-4 h-4 mr-2 text-green-500" />
                <span className="text-green-700">
                  Setup completed {new Date(subscription.setup_completed_at).toLocaleDateString()}
                </span>
              </div>
            )}

            {subscription.hardware_status && (
              <div className="flex items-center text-sm">
                {getHardwareStatusIcon(subscription.hardware_status)}
                <span className="ml-2 capitalize">{subscription.hardware_status.replace('_', ' ')}</span>
                {subscription.hardware_serial && (
                  <span className="ml-2 text-muted-foreground">
                    (S/N: {subscription.hardware_serial})
                  </span>
                )}
              </div>
            )}

            {subscription.last_service_review_at && plan?.includes_ongoing_service && (
              <div className="flex items-center text-sm text-muted-foreground">
                <Clock className="w-4 h-4 mr-2" />
                <span>Last service review: {new Date(subscription.last_service_review_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="pt-2">
          {isTrial || !plan ? (
            <Button 
              onClick={() => navigate('/pricing')} 
              className="w-full rounded-full"
              data-testid="upgrade-plan-btn"
            >
              {isTrial ? 'Upgrade Plan' : 'Choose a Plan'}
            </Button>
          ) : (
            <Button 
              variant="outline" 
              onClick={() => navigate('/pricing')} 
              className="w-full rounded-full"
              data-testid="change-plan-btn"
            >
              Change Plan
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
